import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calendar as CalIcon, StickyNote, Save, Eye, EyeOff } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * FollowupSettings — controls for the weekly show-up recap + post-meeting
 * note nudge emails. Persists to `/api/reminders/followup-settings`.
 */
export default function FollowupSettings() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    api.get("/reminders/followup-settings").then(({ data }) => setState(data?.settings || null)).catch(() => {});
  }, []);

  if (!state) return <div className="text-sm text-[var(--peak-muted)]">Loading…</div>;

  const upd = (patch) => setState({ ...state, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/reminders/followup-settings", state);
      setState(data?.settings || state);
      toast.success("Follow-up settings saved");
    } catch {
      toast.error("Failed to save follow-ups");
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    try {
      if (!previewHtml) {
        const { data } = await api.get("/reminders/recap-preview");
        setPreviewHtml(data?.html || "<p>No preview available</p>");
      }
      setPreviewOpen(true);
    } catch {
      toast.error("Failed to load preview");
    }
  };

  return (
    <div className="space-y-6" data-testid="followup-settings">
      {/* Weekly recap */}
      <div className="pb-6 border-b border-[var(--peak-border)]">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <CalIcon className="w-4 h-4 text-[var(--peak-ink)]" />
              <div className="section-label">Weekly show-up recap</div>
            </div>
            <p className="text-sm text-[var(--peak-muted)] mt-1 max-w-xl">
              A themed email summarizing last week's bookings, attendance, cancellations, reminders sent, and show-up rate.
            </p>
          </div>
          <button
            type="button"
            onClick={() => upd({ recap_enabled: !state.recap_enabled })}
            data-testid="recap-toggle"
            data-active={state.recap_enabled ? "1" : "0"}
            className={`flex-none inline-flex items-center gap-2 px-4 py-2 border-2 font-heading font-bold uppercase text-xs tracking-wider transition-colors ${
              state.recap_enabled
                ? "border-[var(--peak-accent)] bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)]"
                : "border-[var(--peak-ink)] text-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-[var(--peak-bg)]"
            }`}
          >
            {state.recap_enabled ? "On" : "Off"}
          </button>
        </div>

        <div>
          <div className="section-label mb-2">Send on</div>
          <div className="flex flex-wrap gap-2" data-testid="recap-day-picker">
            {DAYS.map((day, idx) => {
              const active = state.recap_day_of_week === idx;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => upd({ recap_day_of_week: idx })}
                  data-testid={`recap-day-${idx}`}
                  className={`px-3 py-1.5 border-2 font-heading font-bold uppercase text-xs tracking-wider transition-colors ${
                    active
                      ? "border-[var(--peak-accent)] bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)]"
                      : "border-[var(--peak-ink)] text-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-[var(--peak-bg)]"
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mt-2">
            Sent 08:00-09:00 UTC · once per week
          </div>
        </div>

        <button
          type="button"
          onClick={openPreview}
          data-testid="recap-preview-btn"
          className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border-2 border-[var(--peak-ink)] font-mono uppercase text-xs tracking-wider text-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-[var(--peak-bg)] transition-colors"
        >
          <Eye className="w-3.5 h-3.5" />
          Preview recap email
        </button>
      </div>

      {/* Note nudge */}
      <div>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <StickyNote className="w-4 h-4 text-[var(--peak-ink)]" />
              <div className="section-label">Post-meeting note nudge</div>
            </div>
            <p className="text-sm text-[var(--peak-muted)] mt-1 max-w-xl">
              After every meeting, if no note is logged within the threshold, we email you a reminder to add one.
            </p>
          </div>
          <button
            type="button"
            onClick={() => upd({ note_nudge_enabled: !state.note_nudge_enabled })}
            data-testid="nudge-toggle"
            data-active={state.note_nudge_enabled ? "1" : "0"}
            className={`flex-none inline-flex items-center gap-2 px-4 py-2 border-2 font-heading font-bold uppercase text-xs tracking-wider transition-colors ${
              state.note_nudge_enabled
                ? "border-[var(--peak-accent)] bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)]"
                : "border-[var(--peak-ink)] text-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-[var(--peak-bg)]"
            }`}
          >
            {state.note_nudge_enabled ? "On" : "Off"}
          </button>
        </div>

        <label className="block max-w-sm">
          <div className="section-label mb-2">Nudge me if no note after</div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={168}
              value={state.note_nudge_hours}
              onChange={(e) => upd({ note_nudge_hours: parseInt(e.target.value || "24", 10) })}
              data-testid="nudge-hours"
              className="brutalist-input flex-1"
            />
            <span className="text-sm text-[var(--peak-muted)] font-mono uppercase tracking-wider">hours</span>
          </div>
        </label>
      </div>

      {/* Save */}
      <div className="pt-4 border-t border-[var(--peak-border)]">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="followup-save"
          className="inline-flex items-center gap-2 px-5 py-2 bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)] font-heading font-bold uppercase text-xs tracking-wider hover:bg-[var(--peak-ink)] hover:text-[var(--peak-bg)] transition-colors disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving…" : "Save follow-up settings"}
        </button>
      </div>

      {/* Preview modal */}
      {previewOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setPreviewOpen(false)}
          data-testid="recap-preview-modal"
        >
          <div
            className="bg-[var(--peak-surface)] border-2 border-[var(--peak-ink)] max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b-2 border-[var(--peak-ink)]">
              <div className="section-label">Recap email preview</div>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="p-1 text-[var(--peak-muted)] hover:text-[var(--peak-ink)]"
                data-testid="recap-preview-close"
              >
                <EyeOff className="w-4 h-4" />
              </button>
            </div>
            <div
              className="p-4"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
